import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { FileDocument, FileDocumentKind } from '@munifocus/contracts-gov';
import { api } from './api-client';
import { useMutationToast } from './mutation-toast';

export interface DocumentFields {
  kind: FileDocumentKind;
  title: string;
  authorName?: string;
  documentDate?: string;
  note?: string;
}

export async function listDocuments(fileId: string): Promise<FileDocument[]> {
  const { data } = await api.get<FileDocument[]>(`/files/${fileId}/documents`);
  return data;
}

export async function uploadDocument(
  fileId: string,
  document: File,
  fields: DocumentFields,
): Promise<FileDocument> {
  // Let the browser set the multipart boundary — don't set Content-Type by hand.
  const form = new FormData();
  form.append('document', document);
  form.append('kind', fields.kind);
  form.append('title', fields.title.trim());
  if (fields.authorName?.trim()) form.append('authorName', fields.authorName.trim());
  if (fields.documentDate) form.append('documentDate', fields.documentDate);
  if (fields.note?.trim()) form.append('note', fields.note.trim());
  const { data } = await api.post<FileDocument>(`/files/${fileId}/documents`, form);
  return data;
}

export async function deleteDocument(documentId: string): Promise<void> {
  await api.delete(`/files/documents/${documentId}`);
}

export const documentKeys = {
  forFile: (fileId: string) => ['documents', fileId] as const,
};

export function useDocuments(fileId: string | undefined) {
  return useQuery({
    queryKey: documentKeys.forFile(fileId ?? ''),
    queryFn: () => listDocuments(fileId as string),
    enabled: !!fileId,
  });
}

export function useUploadDocument(fileId: string | undefined) {
  const qc = useQueryClient();
  const toast = useMutationToast();
  return useMutation({
    mutationFn: ({ document, fields }: { document: File; fields: DocumentFields }) =>
      uploadDocument(fileId as string, document, fields),
    onSuccess: (document) => {
      if (fileId) qc.invalidateQueries({ queryKey: documentKeys.forFile(fileId) });
      toast.success('gov.documents.uploadedToast', { title: document.title });
    },
    onError: (err) => toast.error(err, 'gov.documents.uploadError'),
  });
}

export function useDeleteDocument(fileId: string | undefined) {
  const qc = useQueryClient();
  const toast = useMutationToast();
  return useMutation({
    mutationFn: (documentId: string) => deleteDocument(documentId),
    onSuccess: () => {
      if (fileId) qc.invalidateQueries({ queryKey: documentKeys.forFile(fileId) });
      toast.success('gov.documents.deletedToast');
    },
    onError: (err) => toast.error(err, 'gov.documents.deleteError'),
  });
}
